import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FocusEvent } from "react";
import { Logomark, Wordmark } from "@/components/brand";
import { cn } from "@/lib/cn";
import { PRIMARY_NAV, type NavItem } from "./nav-config";

const COPY_REVEAL_DELAY = 100;

/**
 * Tablet navigation: a compact 64px rail that expands in place as a layered
 * overlay. The overlay never reflows page content, so the tablet layout stays
 * stable while labels become available on hover or keyboard focus.
 */
export function IconRail({ items = PRIMARY_NAV }: { items?: NavItem[] }) {
  const [expanded, setExpanded] = useState(false);
  const [copyVisible, setCopyVisible] = useState(false);
  const railRef = useRef<HTMLElement>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setRailExpanded = useCallback((nextExpanded: boolean) => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);

    setExpanded(nextExpanded);
    if (!nextExpanded) {
      setCopyVisible(false);
      return;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setCopyVisible(true);
      return;
    }

    copyTimerRef.current = setTimeout(() => setCopyVisible(true), COPY_REVEAL_DELAY);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setRailExpanded(false);
        railRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, [setRailExpanded]);

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setRailExpanded(false);
  };

  return (
    <aside
      ref={railRef}
      tabIndex={-1}
      aria-label="Primary navigation"
      data-testid="icon-rail"
      onMouseEnter={() => setRailExpanded(true)}
      onMouseLeave={() => setRailExpanded(false)}
      onFocus={(event) => {
        if (event.target !== event.currentTarget) setRailExpanded(true);
      }}
      onBlur={handleBlur}
      className="group relative z-40 hidden w-16 shrink-0 outline-none md:flex"
    >
      <svg width="0" height="0" aria-hidden="true" className="absolute">
        <defs>
          {/* Top-to-bottom: light blue at top fading down to a brighter medium blue. */}
          <linearGradient id="icon-rail-active-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#60A5FA" />
            <stop offset="100%" stopColor="#3B82F6" />
          </linearGradient>
        </defs>
      </svg>
      <div
        data-testid="icon-rail-panel"
        data-expanded={expanded}
        data-copy-visible={copyVisible}
        className={cn(
          "absolute inset-y-0 left-0 flex flex-col overflow-hidden py-4 transition-[width,box-shadow,background-color,border-color] duration-[180ms] ease-[var(--ease-standard)] motion-reduce:transition-none",
          expanded
            ? "w-60 border-r border-border bg-surface-2 shadow-[var(--shadow-popover)]"
            : "w-16 border-r border-transparent bg-transparent shadow-none",
        )}
      >
        <Link
          to="/feed"
          aria-label="Studside home"
          className="relative mx-auto mb-4 flex h-8 w-full shrink-0 items-center overflow-hidden px-4"
        >
          <span className="flex size-8 shrink-0 items-center justify-center">
            <Logomark size="lg" />
          </span>
          <span
            aria-hidden={!copyVisible}
            className={cn(
              "invisible absolute left-12 flex h-8 shrink-0 translate-x-1 items-center opacity-0 transition-[opacity,transform,visibility] duration-100 ease-out motion-reduce:transition-none",
            copyVisible && "visible translate-x-0 opacity-100",
            )}
          >
            <Wordmark size="lg" />
          </span>
        </Link>
        <nav className="flex flex-col gap-1 px-2">
          {items.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              preload="intent"
              activeOptions={{ exact: false, includeSearch: false }}
              aria-label={item.label}
              className="group flex h-10 w-full items-center gap-3 rounded-sm py-0 pr-3 pl-3 text-body font-medium text-text-secondary transition-colors duration-100 hover:bg-surface-3 hover:text-text-primary active:bg-surface-3"
              activeProps={{
                className: "bg-surface-3! text-text-primary!",
              }}
            >
              {({ isActive }: { isActive: boolean }) => (
                <>
                  <span className="flex size-6 shrink-0 items-center justify-center">
                    <item.icon
                      size={24}
                      weight={isActive ? "fill" : "bold"}
                      className={cn(
                        "transition-colors duration-100",
                        !isActive && "text-text-secondary group-hover:text-text-primary",
                      )}
                      style={isActive ? { fill: "url(#icon-rail-active-gradient)" } : undefined}
                    />
                  </span>
                  <span
                    data-rail-label=""
                    aria-hidden={!copyVisible}
                    className={cn(
                      "invisible whitespace-nowrap translate-x-1 opacity-0 transition-[opacity,transform,visibility] duration-100 ease-out motion-reduce:transition-none",
                      copyVisible && "visible translate-x-0 opacity-100",
                    )}
                  >
                    {item.label}
                  </span>
                </>
              )}
            </Link>
          ))}
        </nav>
      </div>
    </aside>
  );
}
